/**
 * DocumentsTab - Registru Documente cu Foldere
 * Refactorizat pentru a folosi componenta separată ContractGeneratorDialog
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { safeOpenHTMLWindow } from "@/utils/safeHTML";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  FileText, Download, Trash2, Calendar, User, Hash, Eye, 
  FolderOpen, Car, Palmtree, FileSignature, Shield, Users,
  Upload, Search, ChevronRight, ArrowLeft, Plus, Building,
  Wand2, FileOutput, Loader2, Edit, FolderPlus, XCircle, EyeOff, Mail
} from "lucide-react";
import { HelpTooltip } from "@/components/HelpTooltip";
import ContractGeneratorDialog from "./contracts/ContractGeneratorDialog";
import ContractDetailPanel from "./contracts/ContractDetailPanel";
import { generateContractFilename } from "./contracts/contractUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configurare foldere
const FOLDERS = [
  { id: 'contract_servicii', name: 'Contracte Prestări Servicii', icon: Building, color: 'indigo', description: 'Contracte cu clienții pentru șantiere', hasGenerator: true },
  { id: 'trip_sheet', name: 'Foi de Parcurs', icon: Car, color: 'blue', description: 'Foile de parcurs ale vehiculelor' },
  { id: 'leave_request', name: 'Cereri de Concediu', icon: Palmtree, color: 'green', description: 'Cereri CO, CM, CS, FS' },
  { id: 'contract', name: 'Contracte de Muncă', icon: FileSignature, color: 'purple', description: 'Contractele angajaților' },
  { id: 'ssm_psi', name: 'Fișe SSM și PSI', icon: Shield, color: 'amber', description: 'Documente securitate și sănătate' },
  { id: 'other', name: 'Alte Documente', icon: FileText, color: 'slate', description: 'Documente diverse' }
];

const folderColors = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-700' }
};

export default function DocumentsTab({ documents, documentStats, onRefresh, companyId, workers, sites, onNavigateToDeviz }) {
  const [activeFolder, setActiveFolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    document_type: '',
    worker_id: '',
    site_id: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  
  // State pentru Generator Contracte (folosind componenta refactorizată)
  const [showContractGenerator, setShowContractGenerator] = useState(false);
  const [editingContractId, setEditingContractId] = useState(null);
  const [editingContractData, setEditingContractData] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [autoSendContract, setAutoSendContract] = useState(false);
  const [companyData, setCompanyData] = useState(null);

  // UI-only soft-delete: contractele ascunse din UI dar păstrate în DB.
  // Cheia este per-companie pentru izolare multi-tenant.
  const hiddenContractsKey = `etesa_hidden_contracts_${companyId || 'global'}`;
  const [uiHiddenContractIds, setUiHiddenContractIds] = useState(() => {
    try {
      const raw = localStorage.getItem(`etesa_hidden_contracts_${companyId || 'global'}`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const [showHiddenContracts, setShowHiddenContracts] = useState(false);

  const persistHiddenIds = (set) => {
    try {
      localStorage.setItem(hiddenContractsKey, JSON.stringify(Array.from(set)));
    } catch { /* localStorage might be unavailable in some private modes */ }
  };

  const handleUiHideContract = (doc) => {
    if (!window.confirm(
      `Ștergi definitiv contractul "${doc.title}" din lista UI?\n\n` +
      `• Contractul rămâne în baza de date (nu se șterge fizic).\n` +
      `• Numărul de înregistrare (#${doc.registration_number}) NU este eliberat — apare ca "gol" în listă, astfel știi câte ai ascuns.\n` +
      `• Poți afișa contractele ascunse oricând cu butonul "Afișează ascunse" din lista de contracte.`
    )) return;
    const next = new Set(uiHiddenContractIds);
    next.add(doc.id);
    setUiHiddenContractIds(next);
    persistHiddenIds(next);
    toast.success('Contract ascuns din UI');
  };

  const handleUiUnhideContract = (doc) => {
    const next = new Set(uiHiddenContractIds);
    next.delete(doc.id);
    setUiHiddenContractIds(next);
    persistHiddenIds(next);
    toast.success('Contract reafișat');
  };
  // Încarcă datele firmei pentru generator
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyId) return;
      try {
        const response = await axios.get(`${API}/companies/${companyId}`);
        setCompanyData(response.data);
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    };
    fetchCompanyData();
  }, [companyId]);

  // Grupăm documentele pe tip
  const documentsByFolder = FOLDERS.reduce((acc, folder) => {
    acc[folder.id] = (documents || []).filter(doc => doc.document_type === folder.id);
    return acc;
  }, {});

  // Documente pentru folderul activ sau toate
  const filteredDocumentsRaw = activeFolder 
    ? documentsByFolder[activeFolder] 
    : documents || [];

  // Aplicăm filtrul UI-hidden doar pentru folderul de contracte. În alte
  // foldere nu folosim acest mecanism pentru moment.
  const filteredDocuments = activeFolder === 'contract_servicii' && !showHiddenContracts
    ? filteredDocumentsRaw.filter(d => !uiHiddenContractIds.has(d.id))
    : filteredDocumentsRaw;

  const hiddenContractsInActiveFolder = activeFolder === 'contract_servicii'
    ? (documentsByFolder['contract_servicii'] || []).filter(d => uiHiddenContractIds.has(d.id))
    : [];

  // Filtrare după search
  const searchedDocuments = filteredDocuments.filter(doc =>
    doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.registration_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Sigur doriți să ștergeți acest document?")) return;
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success("Document șters!");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleDeleteContract = async (doc) => {
    // Pentru contracte: backend decide soft-delete (ANULAT) vs ștergere fizică
    const ok = window.confirm(
      "Vrei să ștergi acest contract?\n\n" +
      "• Dacă este ULTIMUL contract: ștergere completă (numărul se eliberează).\n" +
      "• Dacă este INTERMEDIAR: va fi marcat ca \"ANULAT\" și numerotarea celorlalte rămâne intactă."
    );
    if (!ok) return;
    try {
      const r = await axios.delete(`${API}/contracts/${doc.id}`, {
        params: companyId ? { company_id: companyId } : undefined
      });
      toast.success(r.data?.message || "Operațiune efectuată");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const viewDocument = (doc) => {
    if (doc.html_content) {
      safeOpenHTMLWindow(doc.html_content);
    } else if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    } else {
      toast.error("Documentul nu are conținut pentru vizualizare");
    }
  };

  const downloadDocument = async (doc) => {
    if (doc.contract_data && doc.html_content) {
      toast.info('Se generează PDF-ul...');
      try {
        let cData;
        try {
          cData = typeof doc.contract_data === 'string' ? JSON.parse(doc.contract_data) : doc.contract_data;
        } catch (error) { cData = {}; }

        const filename = generateContractFilename(cData, cData.beneficiar_name);
        
        const response = await axios.post(`${API}/documents/generate-pdf`, {
          html_content: doc.html_content,
          filename: filename
        }, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF generat cu succes!');
      } catch (error) {
        console.error('Eroare la generarea PDF:', error);
        toast.error('Eroare la generarea PDF-ului');
      }
    } else if (doc.html_content) {
      const blob = new Blob([doc.html_content], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `document_${doc.registration_number}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  const handleUpload = async () => {
    if (!uploadData.title || !uploadData.document_type) {
      toast.error("Completați titlul și tipul documentului");
      return;
    }

    setUploading(true);
    try {
      let fileUrl = null;
      
      // Dacă avem fișier, îl încărcăm
      if (uploadData.file) {
        const formData = new FormData();
        formData.append('file', uploadData.file);
        
        const uploadResponse = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        fileUrl = uploadResponse.data.url;
      }

      // Găsim numele șantierului dacă e selectat
      const selectedSite = sites?.find(s => s.id === uploadData.site_id);

      // Creăm documentul
      await axios.post(`${API}/documents`, {
        title: uploadData.title,
        document_type: uploadData.document_type,
        file_url: fileUrl,
        worker_id: uploadData.worker_id || null,
        worker_name: uploadData.worker_id ? workers?.find(w => w.id === uploadData.worker_id)?.name : null,
        site_id: uploadData.site_id || null,
        site_name: selectedSite?.name || null
      }, { params: { company_id: companyId } });

      toast.success("Document încărcat cu succes!");
      setShowUploadDialog(false);
      setUploadData({ title: '', document_type: '', worker_id: '', site_id: '', file: null });
      onRefresh();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Eroare la încărcarea documentului");
    } finally {
      setUploading(false);
    }
  };

  const getFolderInfo = (folderId) => FOLDERS.find(f => f.id === folderId);


  // Editare contract existent
  const handleEditContract = (doc) => {
    if (doc.contract_data) {
      setEditingContractId(doc.id);
      setEditingContractData(doc.contract_data);
      setShowContractGenerator(true);
    } else {
      toast.error("Contractul nu are date salvate pentru editare");
    }
  };

  const handleGenerateProject = async (contractDoc) => {
    if (contractDoc.project_generated) {
      if (!confirm("Acest contract are deja un proiect generat. Dorești să generezi un proiect nou?")) return;
    }
    try {
      const response = await axios.post(`${API}/projects/from-contract/${contractDoc.id}?company_id=${companyId}`);
      const { site, phases } = response.data;
      toast.success(`Proiect "${site.name}" creat cu ${phases.length} etape!`);
      onRefresh();
    } catch (error) {
      console.error("Eroare la generare proiect:", error);
      toast.error("Eroare la generarea proiectului din contract");
    }
  };

  return (
    <>
    {/* Contract Detail Panel - takes over the full tab */}
    {selectedContract && (
      <ContractDetailPanel
        contract={selectedContract}
        companyId={companyId}
        autoOpenSend={autoSendContract}
        onBack={() => { setSelectedContract(null); setAutoSendContract(false); }}
        onRefresh={() => { setSelectedContract(null); setAutoSendContract(false); onRefresh(); }}
        onNavigateToDeviz={onNavigateToDeviz}
      />
    )}

    {!selectedContract && (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}} className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-600" />
              {activeFolder ? (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setActiveFolder(null)}
                    className="p-1 h-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  {getFolderInfo(activeFolder)?.name}
                </>
              ) : (
                'Registru Documente'
              )}
              <HelpTooltip section="documente" />
            </CardTitle>
            <CardDescription>
              {activeFolder 
                ? getFolderInfo(activeFolder)?.description 
                : 'Organizare pe categorii cu numerotare automată'}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowUploadDialog(true)}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              Încarcă Document
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Vista Foldere */}
        {!activeFolder ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {FOLDERS.map((folder) => {
              const IconComponent = folder.icon;
              const colors = folderColors[folder.color];
              const count = documentsByFolder[folder.id]?.length || 0;
              
              return (
                <button
                  key={folder.id}
                  onClick={() => setActiveFolder(folder.id)}
                  className={`p-6 rounded-xl border-2 ${colors.border} ${colors.bg} hover:shadow-lg transition-all text-left group`}
                  data-testid={`folder-${folder.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <IconComponent className={`w-8 h-8 ${colors.text}`} />
                    <Badge className={colors.badge}>{count}</Badge>
                  </div>
                  <h3 className={`font-semibold ${colors.text}`}>{folder.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{folder.description}</p>
                  <ChevronRight className={`w-4 h-4 ${colors.text} mt-2 opacity-0 group-hover:opacity-100 transition-opacity`} />
                </button>
              );
            })}
          </div>
        ) : (
          /* Vista Documente din Folder */
          <div className="space-y-4">
            {/* Search și Buton Generator */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Caută document..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {activeFolder === 'contract_servicii' && (
                <Button 
                  onClick={() => setShowContractGenerator(true)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Generator Contracte
                </Button>
              )}
            </div>

            {/* Toggle pentru contractele ascunse din UI (UI-only soft delete) */}
            {activeFolder === 'contract_servicii' && hiddenContractsInActiveFolder.length > 0 && (
              <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs sm:text-sm">
                <div className="flex items-center gap-2 text-amber-800">
                  <EyeOff className="w-4 h-4" />
                  <span>
                    <strong>{hiddenContractsInActiveFolder.length}</strong> {hiddenContractsInActiveFolder.length === 1 ? 'contract ascuns' : 'contracte ascunse'} din UI (rămân în DB)
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowHiddenContracts(!showHiddenContracts)}
                  className="text-amber-800 border-amber-300 hover:bg-amber-100"
                  data-testid="toggle-hidden-contracts"
                >
                  {showHiddenContracts ? 'Ascunde din nou' : 'Afișează ascunse'}
                </Button>
              </div>
            )}

            <ScrollArea className="h-[450px]">
              {searchedDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Nu există documente în acest folder</p>
                  <Button 
                    className="mt-4"
                    variant="outline"
                    onClick={() => {
                      setUploadData({...uploadData, document_type: activeFolder});
                      setShowUploadDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă primul document
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {searchedDocuments.map((doc) => {
                    const folderInfo = getFolderInfo(doc.document_type);
                    const colors = folderColors[folderInfo?.color || 'slate'];
                    
                    return (
                      <div 
                        key={doc.id} 
                        className={`p-4 rounded-lg border ${colors.border} ${colors.bg} hover:shadow-md transition-shadow ${activeFolder === 'contract_servicii' && doc.contract_data ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if (activeFolder === 'contract_servicii' && doc.contract_data) {
                            setSelectedContract(doc);
                          }
                        }}
                        data-testid={`document-card-${doc.id}`}
                      >
                        <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0 w-full">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-violet-600 text-white">
                                <Hash className="w-3 h-3 mr-1" />
                                {doc.registration_number}
                              </Badge>
                              {doc.worker_name && (
                                <Badge variant="outline" className="border-slate-300">
                                  <User className="w-3 h-3 mr-1" />
                                  {doc.worker_name}
                                </Badge>
                              )}
                              {doc.site_name && (
                                <Badge variant="outline" className="border-indigo-300 text-indigo-700">
                                  <Building className="w-3 h-3 mr-1" />
                                  {doc.site_name}
                                </Badge>
                              )}
                            </div>
                            <h4 className="font-semibold text-lg mt-2 flex items-center gap-2 flex-wrap">
                              {doc.title}
                              {doc.status === 'ANULAT' && (
                                <Badge className="bg-red-100 text-red-700 border-red-300 font-bold text-xs" data-testid={`anulat-badge-${doc.id}`}>
                                  ANULAT
                                </Badge>
                              )}
                              {uiHiddenContractIds.has(doc.id) && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-bold text-xs" data-testid={`ui-hidden-badge-${doc.id}`}>
                                  ASCUNS DIN UI
                                </Badge>
                              )}
                            </h4>
                            {doc.description && (
                              <p className="text-sm text-slate-600 mt-1">{doc.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(doc.created_at).toLocaleDateString('ro-RO', { 
                                  day: 'numeric', 
                                  month: 'long', 
                                  year: 'numeric' 
                                })}
                              </span>
                              {doc.user_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {doc.user_name}
                                </span>
                              )}
                              {doc.project_generated && (
                                <span className="flex items-center gap-1 text-emerald-600 font-medium">
                                  <FolderPlus className="w-3 h-3" />
                                  Proiect generat
                                </span>
                              )}
                              {activeFolder === 'contract_servicii' && doc.contract_data && (
                                <span className="flex items-center gap-1 text-indigo-500 font-medium">
                                  <ChevronRight className="w-3 h-3" />
                                  Click pentru anexe
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1.5 flex-wrap w-full sm:w-auto sm:justify-end" onClick={(e) => e.stopPropagation()}>
                            {(doc.html_content || doc.file_url) && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => viewDocument(doc)}
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 sm:px-3 gap-0 sm:gap-1"
                                  title="Vizualizare"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Vezi</span>
                                  <span className="hidden sm:inline ml-1">Vizualizare</span>
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => downloadDocument(doc)}
                                  className="text-violet-600 border-violet-300 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 sm:px-3 gap-0 sm:gap-1"
                                  title="Descarcă"
                                >
                                  <Download className="w-4 h-4" />
                                  <span className="text-[9px] leading-tight mt-0.5 sm:hidden">PDF</span>
                                  <span className="hidden sm:inline ml-1">Descarcă</span>
                                </Button>
                              </>
                            )}
                            {doc.contract_data && activeFolder === 'contract_servicii' && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => { setSelectedContract(doc); setAutoSendContract(true); }}
                                  className="text-teal-600 border-teal-300 hover:bg-teal-50 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 sm:px-3 gap-0 sm:gap-1"
                                  data-testid={`send-contract-client-${doc.id}`}
                                  title="Trimite linkul către client"
                                >
                                  <Mail className="w-4 h-4" />
                                  <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Trimite</span>
                                  <span className="hidden sm:inline ml-1">Trimite client</span>
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleEditContract(doc)}
                                  className="text-indigo-600 border-indigo-300 hover:bg-indigo-50 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 sm:px-3 gap-0 sm:gap-1"
                                  title="Editare"
                                >
                                  <FileSignature className="w-4 h-4" />
                                  <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Edit</span>
                                  <span className="hidden sm:inline ml-1">Editare</span>
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleGenerateProject(doc)}
                                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 sm:px-3 gap-0 sm:gap-1"
                                  data-testid={`generate-project-${doc.id}`}
                                  title={doc.project_generated ? "Proiect deja generat" : "Generează proiect din contract"}
                                >
                                  <FolderPlus className="w-4 h-4" />
                                  <span className="text-[9px] leading-tight mt-0.5 sm:hidden">{doc.project_generated ? 'Re-Proiect' : 'Proiect'}</span>
                                  <span className="hidden sm:inline ml-1">{doc.project_generated ? 'Regenerează Proiect' : 'Generează Proiect'}</span>
                                </Button>
                              </>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => doc.contract_data && activeFolder === 'contract_servicii' ? handleDeleteContract(doc) : handleDeleteDocument(doc.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 gap-0 sm:gap-1"
                              data-testid={`delete-doc-${doc.id}`}
                              title={doc.contract_data ? "Anulează contract (ANULAT dacă intermediar)" : "Șterge document"}
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="text-[9px] leading-tight mt-0.5 sm:hidden">{doc.contract_data ? 'Anulat' : 'Șterge'}</span>
                              {doc.contract_data && <span className="hidden sm:inline ml-1 text-[11px]">Anulat</span>}
                            </Button>
                            {doc.contract_data && activeFolder === 'contract_servicii' && (
                              uiHiddenContractIds.has(doc.id) ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUiUnhideContract(doc)}
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 gap-0 sm:gap-1"
                                  data-testid={`unhide-contract-${doc.id}`}
                                  title="Reafișează contractul în UI"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Restore</span>
                                  <span className="hidden sm:inline ml-1 text-[11px]">Restore</span>
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUiHideContract(doc)}
                                  className="text-rose-700 hover:text-rose-900 hover:bg-rose-50 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 gap-0 sm:gap-1"
                                  data-testid={`ui-hide-contract-${doc.id}`}
                                  title="Ascunde definitiv din UI (păstrează în DB)"
                                >
                                  <XCircle className="w-4 h-4" />
                                  <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Defin.</span>
                                  <span className="hidden sm:inline ml-1 text-[11px]">Șterge definitiv</span>
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </CardContent>

      {/* Dialog Upload Document */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-violet-600" />
              Încarcă Document Nou
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titlu Document *</Label>
              <Input
                value={uploadData.title}
                onChange={(e) => setUploadData({...uploadData, title: e.target.value})}
                placeholder="ex: Contract de muncă Ion Popescu"
              />
            </div>
            
            <div>
              <Label>Tip Document *</Label>
              <Select 
                value={uploadData.document_type} 
                onValueChange={(v) => setUploadData({...uploadData, document_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectează tipul" />
                </SelectTrigger>
                <SelectContent>
                  {FOLDERS.map(folder => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Angajat (opțional)</Label>
              <Select 
                value={uploadData.worker_id || "none"} 
                onValueChange={(v) => setUploadData({...uploadData, worker_id: v === "none" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectează angajatul" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Fără angajat asociat</SelectItem>
                  {workers?.map(worker => (
                    <SelectItem key={worker.id} value={worker.id}>
                      {worker.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Selectare Proiect (opțional)</Label>
              <Select 
                value={uploadData.site_id || "none"} 
                onValueChange={(v) => setUploadData({...uploadData, site_id: v === "none" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectează proiect" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Fără șantier asociat</SelectItem>
                  {sites?.map(site => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Fișier</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">PDF, DOC, DOCX, JPG, PNG</p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Anulează
              </Button>
              <Button 
                onClick={handleUpload}
                disabled={uploading}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {uploading ? 'Se încarcă...' : 'Încarcă'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Generator Contracte - Componentă Refactorizată */}
      <ContractGeneratorDialog
        open={showContractGenerator}
        onOpenChange={(open) => {
          setShowContractGenerator(open);
          if (!open) {
            setEditingContractId(null);
            setEditingContractData(null);
          }
        }}
        companyId={companyId}
        companyData={companyData}
        editingContractId={editingContractId}
        editingContractData={editingContractData}
        onRefresh={onRefresh}
      />
    </Card>
    )}
    </>
  );
}
